import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonBaseStart = "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none"
const buttonBaseEnd = "shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive"
// New sizes replace the legacy specificity rule; old sizes retain it verbatim.
const legacyIconSizing = "[&_svg:not([class*='size-'])]:size-4"

function createButtonVariants(base: string) {
  return cva(base,
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-ink shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border border-edge bg-raised/50 shadow-xs hover:bg-hover hover:text-ink text-ink-secondary",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost:
          "text-ink-secondary hover:bg-accent hover:text-accent-foreground",
        link: "text-brand underline-offset-4 hover:text-brand-hover hover:underline",
        // STANDARD 2.8 E1a: additive opt-in variants and sizes preserve legacy defaults.
        neutral:
          "bg-raised border border-control-edge text-ink-secondary shadow-none hover:bg-hover hover:text-ink",
        control:
          "bg-raised border border-control-edge text-ink-secondary hover:bg-hover hover:text-ink rounded-control text-label font-medium gap-1.5 px-3 shadow-none [&_svg]:icon transition-[color,background-color,border-color]",
        ai:
          "bg-ai-fill border border-ai-border text-ai-text shadow-ai-glow hover:bg-ai-fill/80 rounded-control",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-sm gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-sm px-6 has-[>svg]:px-4",
        icon: "size-9",
        "control-sm": "min-h-control-sm rounded-control text-label font-medium gap-1.5 px-3 shadow-none [&_svg]:icon transition-[color,background-color,border-color]",
        control: "min-h-control rounded-control text-label font-medium gap-1.5 px-3 shadow-none [&_svg]:icon transition-[color,background-color,border-color]",
        "control-lg": "min-h-control-lg rounded-control text-label font-medium gap-1.5 px-3 shadow-none [&_svg]:icon transition-[color,background-color,border-color]",
      },
    },
    compoundVariants: [
      // Size geometry must not cancel the AI variant's static glow.
      { variant: "ai", size: ["control-sm", "control", "control-lg"], className: "shadow-ai-glow" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
  )
}

const legacyButtonVariants = createButtonVariants(`${buttonBaseStart} ${legacyIconSizing} ${buttonBaseEnd}`)
const controlButtonVariants = createButtonVariants(`${buttonBaseStart} ${buttonBaseEnd}`)

function buttonVariants(props?: Parameters<typeof legacyButtonVariants>[0]) {
  const recipe = props?.size === "control-sm" || props?.size === "control" || props?.size === "control-lg"
    ? controlButtonVariants
    : legacyButtonVariants
  return recipe(props)
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
